/**
 * vendorLocation — is this vendor based where the project is?
 *
 * The project is in one city (Abuja). A vendor based anywhere else bills
 * travel and accommodation on top of whatever their quote says, so a
 * ₦15M quote from Lagos is not comparable to a ₦15M quote from Abuja —
 * neither against each other nor against the category's planned_amount
 * ceiling. This module classifies that, conservatively.
 *
 * Three states, deliberately. A blank/missing base_location is NOT 'local':
 * several shortlisted vendors have no location recorded, and quietly
 * treating those as local is exactly the mispricing this is meant to
 * prevent. Unknown means "we can't assess this yet", and the UI says so.
 */

export type VendorLocality = 'local' | 'out-of-town' | 'unknown';

/** Values people type into a location field that mean "not recorded". */
const BLANK_LOCATION_TOKENS = new Set([
  '', '-', '—', 'n/a', 'na', 'tbc', 'tbd', 'unknown', 'not known',
  'not recorded', 'none', '?', 'confirm', 'to confirm',
]);

/**
 * Service categories — people who physically travel to the project.
 *
 * The travel/accommodation uplift only applies to these. It must NOT apply
 * to goods suppliers (1688 factories, fabric sellers, marketplace sellers,
 * anything procurement-shaped): their freight is already modelled properly
 * and separately as `landing_cost_cents` on procurement orders, so surfacing
 * a second "travel is extra" warning there would both double-count the cost
 * and be factually wrong — a Guangzhou factory does not fly to Abuja.
 *
 * Matched as case-insensitive substrings so real category names like
 * "Decoration & Styling" or "Photography / Videography" still hit.
 */
const SERVICE_CATEGORY_KEYWORDS: readonly string[] = [
  'decor',          // Decoration, Decor & Styling
  'entertain',      // Entertainment, MC & Entertainment
  'photograph',
  'videograph',
  'cater',          // Catering
  'service',
  'church',
  'music',
  'makeup', 'hair',
  'planner', 'planning', 'coordination',
  'mc',
  'usher',
  'security',
  'venue',
  'cake',
];

/**
 * Goods/procurement categories — excluded even if a keyword above brushes
 * them. Freight for these lives on the procurement order, not here.
 */
const GOODS_CATEGORY_KEYWORDS: readonly string[] = [
  'logistics',
  'procurement',
  'shipping',
  'freight',
  'fabric',
  'asoebi', 'aso-ebi', 'aso ebi',
  'inventory',
  'stationery',
  'gift',
  // Both spellings on purpose: the app says "favour" now, but vendor rows
  // categorised before the rename still say "souvenir".
  'favour', 'souvenir',
  'supplies',
];

function normalize(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** The bare city token: "Abuja, FCT" → "abuja", "Abuja (FCT)" → "abuja". */
function cityToken(projectCity: string | null | undefined): string {
  return normalize(projectCity).split(/[,(/|–—-]/)[0].trim();
}

/**
 * Does a service vendor's base location sit outside the project city?
 *
 * Conservative and case-insensitive: any location that *mentions* the
 * project city counts as local, because a vendor listing "Lagos / Abuja"
 * or "Abuja / intl" has an Abuja presence and shouldn't be flagged.
 * Only a location that clearly names somewhere else is 'out-of-town'.
 *
 * @param vendorLocation the vendor's `base_location` / `baseLocation`
 * @param projectCity    the city the project is held in, e.g. "Abuja, FCT"
 */
export function isOutOfTown(
  vendorLocation: string | null | undefined,
  projectCity: string | null | undefined,
): VendorLocality {
  const loc = normalize(vendorLocation);
  if (BLANK_LOCATION_TOKENS.has(loc)) return 'unknown';

  const city = cityToken(projectCity);
  // No project city on record either — nothing to compare against.
  if (!city) return 'unknown';

  // Word-ish boundary so "abuja" matches "wuse, abuja" and "abuja/intl"
  // but not some unrelated word that merely contains it.
  const mentionsCity = new RegExp(`(^|[^a-z0-9])${escapeRegExp(city)}([^a-z0-9]|$)`).test(loc);
  return mentionsCity ? 'local' : 'out-of-town';
}

/** Alias with a name that reads honestly at call sites expecting a tri-state. */
export const classifyVendorLocality = isOutOfTown;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Is this budget category one where a vendor physically travels to the
 * project? Goods/procurement categories are excluded — see
 * GOODS_CATEGORY_KEYWORDS above for why.
 */
export function isServiceCategory(categoryName: string | null | undefined): boolean {
  const name = normalize(categoryName);
  if (!name) return false;
  if (GOODS_CATEGORY_KEYWORDS.some(k => name.includes(k))) return false;
  return SERVICE_CATEGORY_KEYWORDS.some(k => name.includes(k));
}

/** Short, UI-ready explanation for a locality verdict. */
export function vendorLocalityReason(
  locality: VendorLocality,
  vendorLocation: string | null | undefined,
  projectCity: string | null | undefined,
): string {
  const city = (projectCity ?? '').split(',')[0].trim() || 'the project city';
  const where = (vendorLocation ?? '').trim();
  switch (locality) {
    case 'out-of-town':
      return `Based in ${where}, not ${city}. This quote excludes travel and accommodation, so the true cost is higher than the figure shown.`;
    case 'unknown':
      return `This vendor's base location isn't recorded, so travel to ${city} can't be assessed. If they're out of town, the true cost is higher than the figure shown.`;
    case 'local':
    default:
      return `Based in ${city} — no travel or accommodation expected.`;
  }
}

/** Should the quote detail surface a travel-cost notice at all? */
export function shouldWarnTravelCost(
  locality: VendorLocality,
  categoryName: string | null | undefined,
): boolean {
  return isServiceCategory(categoryName) && (locality === 'out-of-town' || locality === 'unknown');
}
